import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import {
  listPhiAccessLog,
  listPhiAccessLogForAdmin,
} from "services/fde/auditService";

const AuditLogPage = () => {
  const { userData, organizationId, isGlobalAdmin } = useAuth();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const canAccess = isGlobalAdmin || userData?.role === "admin";

  const load = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    setErrorMessage("");
    try {
      if (isGlobalAdmin && !institutionId) {
        setRows(await listPhiAccessLogForAdmin());
      } else if (institutionId) {
        const result = await listPhiAccessLog({ institutionId });
        setRows(result.rows);
      } else {
        setRows([]);
      }
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load audit log.");
    } finally {
      setLoading(false);
    }
  }, [canAccess, institutionId, isGlobalAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Institution admin access is required to view the PHI audit log.</Alert>
      </Box>
    );
  }

  if (loading) return <Loading page />;

  return (
    <Stack spacing={3}>
      <DashboardPageHeader
        title="PHI access audit"
        subtitle="Immutable access accounting for session transcripts and CMR reads (HIPAA §164.528)."
      />

      {errorMessage ? (
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      ) : null}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Purpose</TableCell>
              <TableCell>Document</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" variant="body2">
                    No access events recorded yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.timestampLabel}</TableCell>
                  <TableCell>{row.uid}</TableCell>
                  <TableCell>{row.role || "—"}</TableCell>
                  <TableCell>{row.action}</TableCell>
                  <TableCell>{row.purpose}</TableCell>
                  <TableCell>{row.documentPath}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
};

export default AuditLogPage;
